import { Package, Trash2, Calendar, DollarSign, Layers } from 'lucide-react';
import type { SavedPackage } from '../../hooks/useSavedPackages';
import { formatPrice } from '../../lib/formatters';

interface Props {
  packages: SavedPackage[];
  selectedId: string | null;
  onSelect: (pkg: SavedPackage) => void;
  onDelete: (id: string) => void;
  loading: boolean;
}

export function SavedPackageSelector({ packages, selectedId, onSelect, onDelete, loading }: Props) {
  if (loading) {
    return (
      <div className="card text-center py-8">
        <div className="animate-pulse text-sm text-muted">Loading saved packages...</div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="card text-center py-10 border-dashed border-2 border-border">
        <Package size={28} className="text-muted mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-primary mb-1">No Saved Packages</h3>
        <p className="text-xs text-muted max-w-sm mx-auto">
          Go to the Package Builder tab, configure a package, and click the Save button to start building your marketing strategy.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">Step 1: Select a Saved Package</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {packages.map(pkg => (
          <button
            key={pkg.id}
            onClick={() => onSelect(pkg)}
            className={`text-left p-4 rounded-xl border-2 transition-all ${
              selectedId === pkg.id
                ? 'border-chart-teal bg-chart-teal/5 shadow-sm'
                : 'border-border hover:border-chart-teal/30 hover:bg-surface/50'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-bold text-primary">{pkg.name}</h4>
              <button
                onClick={e => { e.stopPropagation(); onDelete(pkg.id); }}
                className="p-1 text-muted hover:text-metric-negative transition-colors rounded"
                title="Delete"
              >
                <Trash2 size={12} />
              </button>
            </div>
            <div className="space-y-1 text-xs text-secondary">
              <div className="flex items-center gap-1.5">
                <DollarSign size={11} className="text-muted" />
                {formatPrice(pkg.price)}
              </div>
              <div className="flex items-center gap-1.5">
                <Layers size={11} className="text-muted" />
                {pkg.enabled_features.length} of 7 features
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar size={11} className="text-muted" />
                {new Date(pkg.created_at).toLocaleDateString()}
              </div>
            </div>
            {selectedId === pkg.id && (
              <div className="mt-2 text-xs font-semibold text-chart-teal uppercase tracking-wider">Selected</div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
